import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { dataHandlers } from "../../utils/objectHandlers.js";
import { fetchFonts } from "../../Store/reducers/fontsSlice.js";
import WebFont from "webfontloader";
import { useInView } from "react-intersection-observer";
import { setUploadedFonts } from "../../Store/reducers/uploadFontsSlice.js";


const FontItem = ({ font, handleDragStart, handleAddText }) => {
  const fontFamily = font.family || font.font_family;

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });


  useEffect(() => {
   
    if (inView && font.family) {
      WebFont.load({
        google: {
          families: [fontFamily],
        },
      });
    }
  }, [inView, fontFamily, font.family]);

  return (
    <div
      ref={ref}
      className="flex flex-col p-2 cursor-pointer text-[13px] text-black"
      draggable
      onDragStart={(e) => handleDragStart(e, font)}
      onClick={() => handleAddText(font)}
      style={{ fontFamily }} 
    >
      {fontFamily}
    </div>
  );
};

const Fonts = ({ searchQuery }) => {
  const dispatch = useDispatch();
  const fabricCanvas = useSelector((state) => state.fabricCanvas.fabricCanvas);
  const fonts = useSelector((state) => state.fonts.fonts);
  const fontStatus = useSelector((state) => state.fonts.status);
 const uploadedFonts = useSelector((state) => state.uploadfonts);

  // const [uploadedFonts, setUploadedFonts] = useState([

  // ]);

  useEffect(() => {
    if (fontStatus === "idle") {
      dispatch(fetchFonts());
    }
  }, [dispatch, fontStatus]);

  function registerFont(name, file) {
    const blob = URL.createObjectURL(file);
    const ext = file.name.split('.').pop().toLowerCase();
    const format = {
      ttf: "truetype",
      otf: "opentype",
      woff: "woff",
      woff2: "woff2"
    }[ext] || "truetype";
  
    const style = document.createElement("style");
    style.textContent = `
      @font-face {
        font-family: "${name}";
        src: url("${blob}") format("${format}");
        font-weight: normal;
        font-style: normal;
      }
      .font-${name.toLowerCase()} {
        font-family: "${name}", sans-serif;
      }
    `;
    document.head.appendChild(style);
  }

  const handleFontUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const fontName = file.name.replace(/\.[^/.]+$/, "");
    registerFont(fontName, file);
  
    dispatch(
      setUploadedFonts({
        name: fontName,
        styleClass: `font-${fontName.toLowerCase()}`
      })
    );
  };

  const handleDragStart = (event, font) => {
    event.dataTransfer.setData("font", JSON.stringify(font));
  };

  const handleAddText = (font) => {
    if (fabricCanvas) {
      const centerX = fabricCanvas.getWidth() / 2;
      const centerY = fabricCanvas.getHeight() / 2;

      const data = JSON.stringify(font);
      dataHandlers.font(data, fabricCanvas, {
        left: centerX,
        top: centerY,
      });
    }
  };

  // Filter uploaded fonts based on the search query.
  const filteredUploadedFonts = uploadedFonts.filter((font) =>
    font.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter built‑in fonts based on the search query.
  // These fonts come from a combination of Google and admin backend.
  const filteredBuiltInFonts = fonts.filter((font) => {
    const name = font.family || font.font_family;
    return name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div>
      <div className="flex justify-center items-center w-full gap-2">
        <label
          htmlFor="font-upload"
          className="w-[120px] h-[38px] mb-1 flex justify-center items-center bg-white rounded-xl shadow-[0px_1px_0px_0px_rgba(227,227,227,1)_inset,1px_0px_0px_0px_rgba(227,227,227,1)_inset,-1px_0px_0px_0px_rgba(227,227,227,1)_inset,0px_-1px_0px_0px_rgba(181,181,181,1)_inset] font-semibold text-black cursor-pointer"
        >
          Upload Fonts
        </label>
        <input
          type="file"
          id="font-upload"
          accept=".ttf,.otf,.woff,.woff2"
          className="hidden"
          onChange={handleFontUpload}
        />
      </div>

      <div className="h-[1px] w-full bg-gray-200 mt-3" />

      <div className="w-full h-[370px] overflow-y-scroll custom-scrollbar pt-1 pl-4 pr-1">
        <div className="bg-[#F6F5F5] rounded-lg p-3">
          {/* Uploaded fonts section */}
          <div>
            <div className="underline text-[14px] font-semibold text-black ml-2 mt-1">
              Uploaded By You:
            </div>
            {filteredUploadedFonts.map((font, index) => (
              <div
                key={index}
                className={`flex flex-col p-2 cursor-pointer text-[13px] ${font.styleClass}`}
                style={{  fontFamily: font.name  }}
                draggable
                onDragStart={(event) => handleDragStart(event, font)}
                onClick={() => handleAddText(font)}
              >
                {font.name}
              </div>
            ))}
          </div>

          <div className="h-[1px] w-full bg-gray-200 my-2" />

          {/* Built‑in fonts section */}
          <div className="underline font-semibold text-black ml-2 text-[14px]">
            Built-in Fonts:
          </div>
          <div>
            {filteredBuiltInFonts.map((font, index) => (
              <FontItem
                key={index}
                font={font}
                handleDragStart={handleDragStart}
                handleAddText={handleAddText}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Fonts;
